import {BatteryChargeValue, FuelLevelValue, Label, Separator} from 'platform/components';
import {GridItem, Text, Heading, HStack, Show, VStack, Grid, Box} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {defaultTo} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {GetServiceCheckInApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {getLevelIndicatorFlags, levelIndicatorsFor} from '@dms/shared';

import {EMPTY_PLACEHOLDER, isNotNilOrEmpty, suffixTestId, TestIdProps, Nullish} from 'shared/web';

import {parseTechnicalInspectionDate} from '../../utils/parseTechnicalInspectionDate';

/** The check-in stores the fuel tank level as a 0–1 ratio, not as a percent. */
const MAX_FUEL_TANK_RATIO = 1;

/** Six steps are 24px — the line box of the `base` `Text` the sibling cells render. */
const VALUE_LINE_HEIGHT = 6;

interface CheckInHandOverParameterProps extends TestIdProps {
  data?: GetServiceCheckInApiResponse;
  parkingPlace?: string | Nullish;
  isCheckin?: boolean;
  /** Of the service case's vehicle — decides which recorded levels are shown. */
  fuelType?: string | Nullish;
}

export function CheckInHandOverParameter(props: CheckInHandOverParameterProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();

  const {hasFuel, hasBattery} = getLevelIndicatorFlags(levelIndicatorsFor(props.fuelType));

  // A stored value the API date format cannot represent would make formatDateTime throw.
  const technicalInspectionDate = parseTechnicalInspectionDate(props.data?.technicalInspection);

  const driverPhoneNumber = props.data?.driver?.phoneNumber;
  // A stored phone object can exist with empty parts, and formatPhoneNumber echoes back whatever
  // it cannot parse — so an unguarded template literal renders "undefined undefined".
  const formattedDriverPhoneNumber = isNotNilOrEmpty(driverPhoneNumber?.number)
    ? formatPhoneNumber(`${driverPhoneNumber?.prefix ?? ''}${driverPhoneNumber?.number}`)
    : null;

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('entity.workshop.labels.vehicleDriver')}</Heading>
      <Grid columns={[2, 2, 4, 4]}>
        <GridItem>
          <Label>{i18n.t('general.labels.nameAndSurname')}</Label>
          <Text data-testid={suffixTestId('driverName', props)}>{props.data?.driver?.name}</Text>
        </GridItem>
        <GridItem>
          <Label>{i18n.t('entity.phoneNumber.labels.number')}</Label>
          <Text data-testid={suffixTestId('driverPhoneNumber', props)}>
            {defaultTo(EMPTY_PLACEHOLDER, formattedDriverPhoneNumber)}
          </Text>
        </GridItem>
        <GridItem>
          <Label>{i18n.t('general.labels.emailAddress')}</Label>
          <Text data-testid={suffixTestId('driverEmail', props)}>
            {defaultTo(EMPTY_PLACEHOLDER, props.data?.driver?.email)}
          </Text>
        </GridItem>
        <GridItem>
          <Label>{i18n.t('general.labels.identityCard')}</Label>
          <Text data-testid={suffixTestId('driverIdentityCard', props)}>
            {defaultTo(EMPTY_PLACEHOLDER, props.data?.driver?.identityCard)}
          </Text>
        </GridItem>
      </Grid>
      <Separator spacing={0} />
      <Heading size={4}>{i18n.t('entity.workshop.labels.vehicleInformation')}</Heading>
      <Grid columns={[2, 2, 4, 4]}>
        <GridItem>
          <Label>{i18n.t('entity.vehicle.labels.actualMileage')}</Label>
          <Text data-testid={suffixTestId('mileage', props)}>
            {defaultTo(EMPTY_PLACEHOLDER, formatNumber(props.data?.mileage))}
          </Text>
        </GridItem>
        <GridItem>
          <Label>{i18n.t('entity.workshop.labels.technicalInspectionValidUntil')}</Label>
          <Text data-testid={suffixTestId('technicalInspection', props)}>
            {isNotNil(technicalInspectionDate)
              ? formatDateTime('dateShort', technicalInspectionDate)
              : EMPTY_PLACEHOLDER}
          </Text>
        </GridItem>
        <Show when={hasFuel}>
          <GridItem>
            <Label>{i18n.t('entity.workshop.labels.fuelTank')}</Label>
            <HStack align="center" height={VALUE_LINE_HEIGHT}>
              <FuelLevelValue
                value={props.data?.fuelTank}
                max={MAX_FUEL_TANK_RATIO}
                data-testid={suffixTestId('fuelTank', props)}
              />
            </HStack>
          </GridItem>
        </Show>
        <Show when={hasBattery}>
          <GridItem>
            <Label>{i18n.t('entity.vehicle.labels.batteryCharge')}</Label>
            <HStack align="center" height={VALUE_LINE_HEIGHT}>
              <BatteryChargeValue
                value={props.data?.batteryCharge}
                data-testid={suffixTestId('batteryCharge', props)}
              />
            </HStack>
          </GridItem>
        </Show>
      </Grid>
      <Show when={props.isCheckin}>
        <Grid columns={[2, 2, 4, 4]}>
          <GridItem>
            <Label>{i18n.t('general.labels.parkingPlace')}</Label>
            <Text data-testid={suffixTestId('parkingPlace', props)}>
              {defaultTo(EMPTY_PLACEHOLDER, props.parkingPlace)}
            </Text>
          </GridItem>
          <GridItem>
            <Label>{i18n.t('entity.order.labels.consentToTestDrive')}</Label>
            <Text data-testid={suffixTestId('consentToTestDrive', props)}>
              {props.data?.consentToTestDrive
                ? i18n.t('general.labels.yes')
                : i18n.t('general.labels.no')}
            </Text>
          </GridItem>
          <GridItem>
            <Label>{i18n.t('entity.order.labels.vehicleIsNotInService')}</Label>
            <Text data-testid={suffixTestId('vehicleIsNotInService', props)}>
              {props.data?.isVehicleNotInService
                ? i18n.t('general.labels.yes')
                : i18n.t('general.labels.no')}
            </Text>
          </GridItem>
          <GridItem>
            <Label>{i18n.t('entity.order.labels.isNonDrivableVehicle')}</Label>
            <Text data-testid={suffixTestId('isNonDrivableVehicle', props)}>
              {props.data?.isNonDrivableVehicle
                ? i18n.t('general.labels.yes')
                : i18n.t('general.labels.no')}
            </Text>
          </GridItem>
        </Grid>
      </Show>
      <Separator spacing={0} />
      <Box>
        <Label>{i18n.t('general.labels.note')}</Label>
        <Text data-testid={suffixTestId('note', props)}>
          {defaultTo(EMPTY_PLACEHOLDER, props.data?.note)}
        </Text>
      </Box>
    </VStack>
  );
}
